import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useIsMobile } from './ui/use-mobile';

export function ScrollIndicator() {
  const [showIndicator, setShowIndicator] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const checkScrollIndicator = () => {
      const scrollPosition = window.scrollY;
      const isScrollable = document.documentElement.scrollHeight > window.innerHeight + 100;
      
      // Show indicator only when:
      // 1. Page is scrollable (has content below the fold)
      // 2. User is at the very top (scrollPosition <= 20 for some tolerance)
      // 3. Not on mobile device (hide completely on mobile)
      const shouldShow = isScrollable && scrollPosition <= 20 && !isMobile;
      
      setShowIndicator(shouldShow);
    };

    // Check immediately when component mounts
    checkScrollIndicator();

    // Check on scroll with throttling for performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkScrollIndicator();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Check on resize and content changes
    const handleResize = () => {
      setTimeout(checkScrollIndicator, 100);
    };

    // Delay initial check to ensure content is fully loaded
    const initialCheckTimer = setTimeout(() => {
      checkScrollIndicator();
    }, 1000);

    // Additional check after images potentially load
    const lateCheckTimer = setTimeout(() => {
      checkScrollIndicator();
    }, 3000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    // Check when images load (for dynamic content)
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (img.complete) {
        checkScrollIndicator();
      } else {
        img.addEventListener('load', checkScrollIndicator);
        img.addEventListener('error', checkScrollIndicator); // Handle broken images
      }
    });

    return () => {
      clearTimeout(initialCheckTimer);
      clearTimeout(lateCheckTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      images.forEach(img => {
        img.removeEventListener('load', checkScrollIndicator);
        img.removeEventListener('error', checkScrollIndicator);
      });
    };
  }, [isMobile]);

  const handleScrollDown = () => {
    const viewportHeight = window.innerHeight;
    const targetScroll = Math.min(viewportHeight * 0.8, 600); // Don't scroll too far
    
    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {showIndicator && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.8 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 25,
            delay: 2 // Delay appearance by 2 seconds to avoid immediate flash
          }}
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 pointer-events-auto"
        >
          <motion.button
            onClick={handleScrollDown}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            className="group relative"
            aria-label="Scroll down to see more content"
          >
            {/* Subtle outer glow effect with new colors */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.4, 0.6, 0.4],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 rounded-full bg-gradient-to-r from-qa-yellow-400/30 via-qa-blue-500/20 to-qa-yellow-400/30 blur-lg"
            />
            
            {/* Bigger minimalistic button with new colors */}
            <div className="relative bg-qa-yellow-400/95 backdrop-blur-xl border border-qa-yellow-500 rounded-full w-16 h-16 shadow-lg transition-all duration-300 group-hover:bg-qa-yellow-500/95 group-hover:border-qa-yellow-600 group-hover:shadow-xl">
              
              {/* Inner gradient border with new colors */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-qa-blue-600/20 via-qa-yellow-500/20 to-qa-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative flex items-center justify-center h-full">
                {/* Single animated chevron with new color */}
                <motion.div
                  animate={{ 
                    y: [0, 4, 0],
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <ChevronDown className="h-6 w-6 text-qa-blue-900 group-hover:text-qa-blue-800" />
                </motion.div>
              </div>
            </div>
            
            {/* Hover pulse effect with new color theme */}
            <motion.div
              className="absolute inset-0 rounded-full bg-gradient-to-r from-qa-yellow-500/15 to-qa-blue-600/15 border border-qa-yellow-500/50"
              initial={{ scale: 1, opacity: 0 }}
              whileHover={{ scale: 1.1, opacity: 0.7 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}